import {
  FjdLoadingOverlay,
  FjdStack,
  FjdVirtualizedTable,
  FjdVirtualizedTableCell,
  FjdVirtualizedTableCol,
  FjdVirtualizedTableRow,
  FjdCheckbox
} from 'fjd-react-components';
import { formatByte } from '../../../../utils/antragsuebersicht';
import styles from '../../SchreibtischBody/Dokumente/dokumente.module.css';
import React, { useContext, useEffect } from 'react';
import { caseDetailsDataContext } from '../../../../context/Context';
import useCheckboxes, {
  ICheckboxesHook
} from '../../../../hooks/useCheckboxes';
import { useAttachmentsDokumenteTab } from '../../../../hooks/useAttachmentsDokumenteTab';
import BescheidstatusBadge from '../../../shared/BescheidstatusBadge/BescheidstatusBadge';
import { Bescheidstatus } from '../../../../models/Status/Bescheidstatus';
import { ErrorComponentBehoerdenServer } from '../../../shared/Error/ErrorComponent';
import { BEHError } from '../../../../utils/Error/ErrorCode';
import AntragBadge from '../../../shared/AntragBadge/AntragBadge';

interface AntragsDownloadTabelleProps {
  readonly updateQueryParameter: (value: string | undefined) => void;
  readonly setError?: (error: BEHError) => void;
}

function AntragsDownloadTabelle(props: AntragsDownloadTabelleProps) {
  const {
    getIdsAsParam,
    getCheckBoxStateById,
    setCheckBoxState,
    checkBoxState,
    selectAllCheckBox,
    onSelectAllCheckboxes,
    onClickCheckBox
  }: ICheckboxesHook = useCheckboxes();

  useEffect(() => {
    props.updateQueryParameter(getIdsAsParam());
  }, [checkBoxState, getIdsAsParam, props]);

  const caseDetails = useContext(caseDetailsDataContext);
  const initialSubmissionId: string =
    caseDetails.data.initialSubmission.submissionId;

  const { attachments, isValidating, error } = useAttachmentsDokumenteTab({
    caseId: caseDetails.data.caseId
  });
  const parentSetHasErrorFct: ((error: BEHError) => void) | undefined =
    props.setError;

  useEffect(() => {
    setCheckBoxState([]);
    if (attachments) {
      setCheckBoxState(
        attachments.map((att) => {
          return {
            checkboxId: `${att.description}_checkbox`,
            id: att.id,
            checked: true
          };
        })
      );
      return () => {
        setCheckBoxState([]);
      };
    }
  }, [caseDetails, setCheckBoxState, attachments]);

  const documents =
    attachments && !isValidating
      ? attachments.map((document) => {
          return { ...document, isBescheid: document.isBescheid };
        })
      : [];

  useEffect(() => {
    if (error && parentSetHasErrorFct) {
      parentSetHasErrorFct(error);
    }
  }, [error, parentSetHasErrorFct]);

  if (error && parentSetHasErrorFct) {
    return <></>;
  } else if (error) {
    return <ErrorComponentBehoerdenServer />;
  }

  return (
    <FjdLoadingOverlay loading={isValidating}>
      <FjdStack spacing="s">
        <FjdVirtualizedTable
          disableVirtualization
          header={
            <FjdVirtualizedTableRow>
              <FjdVirtualizedTableCol maxWidth="4rem">
                <FjdCheckbox
                  checked={selectAllCheckBox}
                  id={'select_all_files'}
                  onChange={(value) =>
                    onSelectAllCheckboxes(value.target.checked)
                  }
                ></FjdCheckbox>
              </FjdVirtualizedTableCol>
              <FjdVirtualizedTableCol>Name</FjdVirtualizedTableCol>
              <FjdVirtualizedTableCol maxWidth="6rem">
                Typ
              </FjdVirtualizedTableCol>
              <FjdVirtualizedTableCol maxWidth="6rem">
                Größe
              </FjdVirtualizedTableCol>
            </FjdVirtualizedTableRow>
          }
        >
          {documents.length === 0 && (
            <FjdVirtualizedTableRow>
              <FjdVirtualizedTableCell>
                {'Keine Dokumente gefunden.'}
              </FjdVirtualizedTableCell>
            </FjdVirtualizedTableRow>
          )}

          {documents.map((document) => (
            <FjdVirtualizedTableRow key={`document${document.id}`}>
              <FjdVirtualizedTableCell maxWidth="4rem">
                <FjdCheckbox
                  checked={getCheckBoxStateById(document.id).checked}
                  id={getCheckBoxStateById(document.id).checkboxId}
                  onChange={(value) =>
                    onClickCheckBox(value?.target.checked, document.id)
                  }
                ></FjdCheckbox>
              </FjdVirtualizedTableCell>
              <FjdVirtualizedTableCell>
                <FjdStack
                  orientation="horizontal"
                  spacing="xl"
                  className={styles.container}
                >
                  <div style={{ whiteSpace: 'initial' }}>
                    {document.description + document.filetype}
                  </div>
                  {document.isBescheid &&
                    (caseDetails.data.bescheidstatus ===
                      Bescheidstatus.POSITIV ||
                      caseDetails.data.bescheidstatus ===
                        Bescheidstatus.NEGATIV) && (
                      <BescheidstatusBadge
                        bescheidstatus={caseDetails?.data.bescheidstatus}
                        fjdBadgeSize={'xs'}
                        badgeLabel={'Bescheid'}
                      />
                    )}
                  {document.submissionId === initialSubmissionId && (
                    <AntragBadge data-testid="antragBadge" />
                  )}
                </FjdStack>
              </FjdVirtualizedTableCell>
              <FjdVirtualizedTableCell maxWidth="6rem">
                {document.filetype.toUpperCase()}
              </FjdVirtualizedTableCell>
              <FjdVirtualizedTableCell maxWidth="6rem">
                {formatByte(`${document.size}`)}
              </FjdVirtualizedTableCell>
            </FjdVirtualizedTableRow>
          ))}
        </FjdVirtualizedTable>
      </FjdStack>
    </FjdLoadingOverlay>
  );
}

export default AntragsDownloadTabelle;
